import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'
import { useRetryEmail } from '@/queries/clientEmails'
import { useRetryRequests } from '@/queries/clientMeetings'
import { callTimeLabel } from '@/types/clientMeetings'
import type { ClientEmail } from '@/types/clientEmails'
import type { CallRequest, ClientMeeting, RequestSource } from '@/types/clientMeetings'
import { tdClass, thClass } from './dashboardParts'
import { AddToHintButton, DismissRequestButton, RequestActions } from './RequestActions'
import { RequestFlag } from './RequestFlag'
import { stateLabel } from './requestLabels'
import { TicketRefLink } from './TicketRefLink'

// Fix every column but the first, so the table fits its card and the action buttons line up row to row.
const COLUMNS = [
  { heading: 'Raised in', width: '', cell: '' },
  { heading: 'Ticket', width: 'w-[190px]', cell: '' },
  { heading: 'Action', width: 'w-[330px]', cell: '' },
  { heading: 'Flag', width: 'w-[220px]', cell: 'pl-7' },
  { heading: '', width: 'w-[56px]', cell: 'pl-0 pr-5' },
]

/** Who raised a request, the moment in the call, and whether they were chasing something already open. */
export function RaisedBy({ request }: { request: CallRequest }) {
  return (
    <div className="mt-0.5 flex flex-wrap items-center gap-1.5 text-[11.5px] text-etn-text-soft">
      <span>{request.raisedBy ?? 'Who raised it is not clear'}</span>
      {request.url && request.seconds !== null && (
        <a href={request.url} target="_blank" rel="noreferrer" className="font-medium tabular-nums text-etn-accent-text-active hover:underline">
          {callTimeLabel(request.seconds)}
        </a>
      )}
      {request.followUp && <Badge variant="outline">Follow-up</Badge>}
    </div>
  )
}

function linkNote(link: NonNullable<CallRequest['link']>) {
  if (link.created) return 'Created from this request'
  return link.by === 'ai' ? 'Linked automatically' : 'Linked by staff'
}

function TicketCell({ request }: { request: CallRequest }) {
  if (request.link) {
    return (
      <div data-testid="request-ticket">
        <div className="flex items-center gap-2">
          <TicketRefLink target={request.link} />
          {request.link.state && <Badge variant="neutral">{stateLabel(request.link.state)}</Badge>}
        </div>
        <div className="mt-0.5 text-[11.5px] text-etn-text-soft">{linkNote(request.link)}</div>
      </div>
    )
  }
  return (
    <div data-testid="request-ticket">
      <Badge variant="neutral">Not a ticket yet</Badge>
      {request.hint && (
        <div data-testid="request-hint" className="mt-1.5 space-y-1">
          <div className="text-[11.5px] text-etn-text-soft">
            Closest: <TicketRefLink target={request.hint} className="font-medium" /> ({request.hint.confidence}%)
          </div>
          <AddToHintButton request={request} />
        </div>
      )}
    </div>
  )
}

function Notice({ children }: { children: React.ReactNode }) {
  return <div className="flex flex-wrap items-center justify-center gap-2 px-[18px] py-6 text-center text-[13px] text-etn-text-soft">{children}</div>
}

type Parent = { meeting: ClientMeeting; email?: never } | { email: ClientEmail; meeting?: never }

export function RequestsTable({
  companyId,
  companyName,
  trackerEnabled,
  highlightId,
  ...parent
}: Parent & {
  companyId: string
  companyName: string
  trackerEnabled: boolean
  highlightId?: string
}) {
  const retryCall = useRetryRequests()
  const retryEmail = useRetryEmail()
  const { meeting, email } = parent
  const isEmail = Boolean(email)
  const status = email ? email.requestsStatus : meeting!.requestsStatus
  const requests = email ? email.requests : meeting!.requests
  const retrying = isEmail ? retryEmail.isPending : retryCall.isPending
  if (status === 'failed') {
    return (
      <Notice>
        <span>{isEmail ? "Couldn't read this email's requests" : "Couldn't read this call's requests"}</span>
        <Button
          size="xs"
          variant="outline"
          disabled={retrying}
          onClick={() => (email ? retryEmail.mutate({ emailId: email._id }) : retryCall.mutate({ meetingId: meeting!._id }))}
        >
          Try again
        </Button>
      </Notice>
    )
  }
  if (status !== 'done') {
    if (email) return <Notice>Reading email…</Notice>
    return meeting!.summaryStatus === 'processed' ? <Notice>Reading this call for requests</Notice> : null
  }
  if (!requests.length) return <Notice>{isEmail ? 'No client requests in this email' : 'No client requests on this call'}</Notice>

  const source: RequestSource = email
    ? { kind: 'email', _id: email._id, title: email.subject ?? 'No subject', date: email.date }
    : { kind: 'call', _id: meeting!._id, title: meeting!.title, date: meeting!.date }
  const context = { source, companyId, companyName, trackerEnabled }
  const headingOf = (heading: string) => (heading === 'Raised in' ? (isEmail ? 'Raised in this email' : 'Raised in this call') : heading)
  return (
    <div className="overflow-x-auto">
      <table className="w-full table-fixed border-collapse">
        <colgroup>
          {COLUMNS.map(column => (
            <col key={column.heading} className={column.width} />
          ))}
        </colgroup>
        <thead>
          <tr>
            {COLUMNS.map(column => (
              <th key={column.heading} scope="col" className={`${thClass} ${column.cell}`}>
                {headingOf(column.heading)}
              </th>
            ))}
          </tr>
        </thead>
        <tbody className="divide-y divide-etn-border-row">
          {requests.map(request => (
            <tr
              key={request._id}
              data-request={request._id}
              className={cn('hover:bg-etn-row-hover', request.dismissed && 'opacity-60', request._id === highlightId && 'bg-etn-accent-tint')}
            >
              <td className={tdClass}>
                <span className="font-medium text-etn-text" title={request.detail}>
                  {request.title}
                </span>
                <RaisedBy request={request} />
              </td>
              <td className={tdClass}>
                <TicketCell request={request} />
              </td>
              <td className={tdClass}>
                <RequestActions request={request} context={context} />
              </td>
              <td className={`${tdClass} ${COLUMNS[3].cell} whitespace-nowrap`}>
                <RequestFlag request={request} />
              </td>
              <td data-testid="request-dismiss-cell" className={`${tdClass} ${COLUMNS[4].cell} text-right`}>
                <DismissRequestButton request={request} />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}
